import React from 'react';
import {
  GraphCardChart,
  GraphCardChartDisclaimer,
  GraphCardContainer,
  GraphCardCount,
  GraphCardDelta,
  GraphCardDescription,
  GraphCardInfo,
  GraphCardMeta,
  GraphCardNotes,
  GraphCardTitle,
  GraphCardTotal,
} from './graphCard.styles';
import { CircularProgress, decomposeColor, Divider, Skeleton, Tooltip as MUITooltip } from '@mui/material';
import { COLORS } from './colors';
import { DeltaValues, getFormattedMetric, hasImproved, MetricsFormatting, MetricsIds } from '../../../components/helpers/metricsHelpers';
import theme from '../../../components/App/theme';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface GraphCardProps {
  id: MetricsIds;
  name: string;
  loadingTotal: boolean;
  total: number | undefined;
  totalFormatting: MetricsFormatting;
  valueCount?: number;
  countFormatting?: MetricsFormatting;
  description: string;
  loadingGraphData?: boolean;
  delta?: DeltaValues;
  deltaFormatting?: MetricsFormatting;
  deltaLabel?: string;
  graphData?: number[] | undefined;
  graphLabels?: string[] | undefined;
  graphNotes?: string;
  isSelectedMetric: boolean;
  graphDataFormatting?: MetricsFormatting;
}

const GraphCard = React.forwardRef<HTMLDivElement, GraphCardProps>(
  (
    {
      id,
      name,
      loadingTotal,
      total,
      totalFormatting,
      valueCount,
      countFormatting,
      description,
      loadingGraphData = false,
      delta,
      deltaFormatting,
      deltaLabel,
      graphData = undefined,
      graphLabels = undefined,
      graphNotes,
      isSelectedMetric,
      graphDataFormatting,
    },
    ref,
  ) => {
    const metricTotal = total && getFormattedMetric(total, totalFormatting);
    const countTotal = valueCount && countFormatting && getFormattedMetric(valueCount, countFormatting);
    const allEmpty = graphData?.length === 0 || graphData?.every((value) => value === 0 || value === null || isNaN(value));

    const renderDelta = (delta: DeltaValues | undefined): JSX.Element => {
      if (!delta) {
        return <GraphCardDelta>{deltaLabel}</GraphCardDelta>;
      }

      const previousMetricTotal = deltaFormatting && getFormattedMetric(delta.previous, deltaFormatting);
      const noData = delta.previous === 0 && delta.absoluteDifference === 0;
      const noImprovements = delta.previous !== 0 && delta.absoluteDifference === 0;
      const showFormattingDisclaimer = metricTotal === previousMetricTotal;

      if (noData) {
        return <GraphCardDelta>No data available for the selected comparison period.</GraphCardDelta>;
      }

      return (
        <GraphCardDelta>
          {noImprovements ? (
            <span className="no-improvements">0.00%</span>
          ) : (
            <span className={hasImproved(delta) ? 'pos' : 'neg'}>
              {delta.absoluteDifference > 0 ? '+' : ''}
              {getFormattedMetric(delta.relativeDifference, '.00%')}
            </span>
          )}{' '}
          {deltaLabel} ({delta.previous === 0 ? '0' : previousMetricTotal})
          {showFormattingDisclaimer && (
            <MUITooltip
              title={
                <span
                  style={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{
                    __html:
                      'Deltas are calculated from full-precision values. Small differences may appear due to rounding in the displayed absolute metrics.',
                  }}
                />
              }
            >
              <InfoIcon style={{ height: 12, width: 12 }} />
            </MUITooltip>
          )}
        </GraphCardDelta>
      );
    };

    return (
      <GraphCardContainer id={id} ref={ref} isSelectedMetric={isSelectedMetric}>
        <GraphCardTitle>{name}</GraphCardTitle>
        <GraphCardMeta>
          {loadingTotal ? (
            <Skeleton
              variant="text"
              sx={{ fontSize: '28px', bgcolor: `rgba(${decomposeColor(COLORS.brand[40]).values},.5)` }}
              animation="wave"
            />
          ) : (
            <GraphCardTotal>
              {metricTotal}
              {valueCount && <GraphCardCount> ({countTotal})</GraphCardCount>}
            </GraphCardTotal>
          )}
          <GraphCardInfo>
            <GraphCardDescription>{description}</GraphCardDescription>
            {!loadingTotal && renderDelta(delta)}
          </GraphCardInfo>
        </GraphCardMeta>
        <Divider sx={{ my: 2 }} />
        <GraphCardChart>
          {loadingGraphData ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', paddingTop: theme.spacing(4) }}>
              <CircularProgress />
            </div>
          ) : (
            <Bar
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  tooltip: graphDataFormatting
                    ? {
                        callbacks: {
                          label: (ctx) => `${getFormattedMetric(ctx.raw as number, graphDataFormatting)}`,
                        },
                      }
                    : undefined,
                  legend: {
                    display: false,
                  },
                  title: {
                    display: false,
                  },
                },
              }}
              data={{
                labels: graphLabels,
                datasets: [
                  {
                    data: graphData,
                    backgroundColor: COLORS.success[100],
                  },
                ],
              }}
            />
          )}
          {allEmpty && <GraphCardChartDisclaimer />}
        </GraphCardChart>
        {graphNotes && <GraphCardNotes>{graphNotes}</GraphCardNotes>}
      </GraphCardContainer>
    );
  },
);

export default GraphCard;
